import _                             from 'underscore';
import moment, { unitOfTime }        from 'moment/moment';
import { ModalProps, SemanticICONS } from 'semantic-ui-react';

import { CorvalActivityHire, CorvalColor, CorvalCompletionState, CorvalCriticality, CorvalProjectActivity, CorvalProjectActivityUpdateItemInput, CorvalRecurrenceType, FuncOrAsyncFunc, SemanticUIColor, Uuid } from '../types';

import { ProjectService, TimeService }                                                                    from '../services';
import { ACTIVITY_CRITICALITY, ACTIVITY_STATUS, ACTIVITY_STATUS_INFO, ENTITY_COLORIZATION, ENTITY_ICONS } from '../constants/ProductEnums';
import { ProjectActivityUpdateChangeSet }                                                                 from '../views/pages/projects/activities/ProjectActivityUpdateDialog';
import { TIME_UNITS }                                                                                     from '../constants/TimeEnums';
import { withoutType }                                                                                    from '../store/_apolloUtil';
import { ProjectTimelineDateFieldObject }                                                                 from '../services/ProjectService';
import React                                                                                              from "react";
import { useSystemModals }                                                                                from "@src/hooks/useSystem";
import { ProjectActivityBulkUpdateDialog, ProjectActivityBulkUpdateType }                                 from "@src/views/pages/projects/operations/ProjectActivityBulkUpdateDialog";
import { ProjectActivityBulkUpdateMultiDialog }                                                           from "@src/views/pages/projects/operations/ProjectActivityBulkUpdateMultiDialog";
import { getFieldWithProjectIdAccessor }                                                                  from "@src/util";
import { useApolloClient }                                                                                from "@apollo/react-hooks";
import { DecisionEngineQueries }                                                                          from "@src/queries";


export interface ProjectActivityChangeDelta<T> {
    projectActivityId: Uuid,
    oldValue: T,
    newValue: T,
}

export interface ProjectActivityTimingChangeDelta extends ProjectActivityChangeDelta<string> {}

export interface ProjectActivityStatusChangeDelta extends ProjectActivityChangeDelta<ACTIVITY_STATUS> {}

export type ProjectActivityEntityChangeSet = {
    added: CorvalProjectActivity[],
    updated: CorvalProjectActivity[],
    removed: CorvalProjectActivity[],
}

export type ProjectActivityEntityChangeCallback = (
    updatedAttributes: ProjectActivityUpdateChangeSet,
    projectActivityId: Uuid,
    propagationCalled?: boolean,
    propagationResultsData?: any,
    entityChangeSet?: ProjectActivityEntityChangeSet
) => void;

export type StaticProjectActivityEntityChangeCallback = (
    updatedAttributes: ProjectActivityUpdateChangeSet,
    projectActivityId: Uuid,
) => void;

export type ProjectActivityEntitySetChangeCallback = (
    updatedAttributes: Record<Uuid, Partial<CorvalProjectActivity>>
) => void;

export type ProjectActivityStatusChangeCallback = ( deltas: ProjectActivityStatusChangeDelta[] ) => void;

export interface UseProjectActivityHook {

    hasTransitionFromExcluded( delta: ProjectActivityStatusChangeDelta ): boolean

    hasTransitionToExcluded( delta: ProjectActivityStatusChangeDelta ): boolean

    statusTransitionIsActionable( delta: ProjectActivityStatusChangeDelta ): boolean

    statusTransitionSetHasActionableItems( deltas: ProjectActivityStatusChangeDelta[] ): boolean

    periodicRecurrenceStringDescription( item: Partial<CorvalProjectActivity> ): string

    milestoneRecurrenceStringDescription( item: Partial<CorvalProjectActivity> ): string

    hasRecurrence( item: Partial<CorvalProjectActivity> ): boolean,

    isPeriodicRecurrence( item: Partial<CorvalProjectActivity> ): boolean,

    isMilestoneRecurrence( item: Partial<CorvalProjectActivity> ): boolean,

    getProjectActivityTags( item: Partial<CorvalProjectActivity> ): string[],

    getProjectActivityHireTypes( item: Partial<CorvalProjectActivity> ): string[],

    getProjectActivityDuration( item: Partial<CorvalProjectActivity>, asDiscreteObject?: boolean ): string | { durationValue: number, durationUnit: TIME_UNITS },

    getProjectActivityDurationInDays( item: Partial<CorvalProjectActivity> ): number,

    hasLateCompletion( actualEndTimeUtc: string, proposeEndTimeValue: string | null, projectTimelineDateFieldObjects?: ProjectTimelineDateFieldObject[] ): boolean,

    getUpdateProjectActivityInputData( item: Partial<CorvalProjectActivity> ): CorvalProjectActivityUpdateItemInput,

    isCriticalActivity( criticalityValue: CorvalCriticality | ACTIVITY_CRITICALITY ): boolean,

    getCriticalityIcon( criticalityValue: CorvalCriticality | ACTIVITY_CRITICALITY ): SemanticICONS,

    getCriticalityColor( criticalityValue: CorvalCriticality | ACTIVITY_CRITICALITY ): CorvalColor,

    getProjectActivityStatusInfo( item: Pick<Partial<CorvalProjectActivity>, 'completionState' | 'isRecurrence'> ): {
        isNotStarted: boolean,
        isDeferred: boolean,
        isComplete: boolean,
        isInProgress: boolean,
        activityActiveColor: CorvalColor,
        defaultActivityColor: CorvalColor,
        statusColor: CorvalColor,
        statusIcon: SemanticICONS,
        statusLabel: string,
        statusValue: ACTIVITY_STATUS,
    },

    getStatusIcon( completionState: CorvalCompletionState | ACTIVITY_STATUS ): SemanticICONS,

    getStatusColor( completionState: CorvalCompletionState | ACTIVITY_STATUS ): CorvalColor,

    getChangeSetProperties(
        changeSetType: 'recurrences' | 'activityTimingDisplay' | 'activityResourcing' | 'activityTiming',
        projectId?: Uuid,
    ): string,

    bulkUpdateProjectActivities(
        projectId: Uuid,
        projectVersionId: Uuid | null,
        projectActivityIds: Uuid[],
        type: ProjectActivityBulkUpdateType,
        onUpdateComplete?: FuncOrAsyncFunc<[ mutatedActivitySet: Array<CorvalProjectActivity> ], void>,
        onUpdateError?: FuncOrAsyncFunc<[ e: Error ], void>,
        modalProps?: ModalProps
    ): void

    bulkUpdateProjectActivitiesMulti(
        projectId: Uuid,
        projectVersionId: Uuid | null,
        projectActivityIds: Uuid[],
        onUpdateComplete?: FuncOrAsyncFunc<[ mutatedActivitySet: Array<CorvalProjectActivity> ], void>,
        onUpdateError?: FuncOrAsyncFunc<[ e: Error ], void>,
        showOptionsForTypes?: Array<ProjectActivityBulkUpdateType> | boolean,
        modalProps?: ModalProps
    ): void,

    getProjectActivityHasDependencyLoops(
        activityId: Uuid,
        parentIds: Uuid[],
        childIds: Uuid[],
    ): Promise<boolean | null>
}

export const useProjectActivities = (): UseProjectActivityHook => {

    const modalActions = useSystemModals(),
          apolloClient = useApolloClient();

    const hasTransitionFromExcluded: UseProjectActivityHook['hasTransitionFromExcluded'] = ( delta ) => {

        return (
            delta.oldValue === ACTIVITY_STATUS.EXCLUDED &&
            delta.newValue !== ACTIVITY_STATUS.EXCLUDED );
    }

    const hasTransitionToExcluded: UseProjectActivityHook['hasTransitionToExcluded'] = ( delta ) => {

        return (
            delta.oldValue !== ACTIVITY_STATUS.EXCLUDED &&
            delta.newValue === ACTIVITY_STATUS.EXCLUDED );
    }

    const statusTransitionIsActionable: UseProjectActivityHook['statusTransitionIsActionable'] = ( delta ) => {

        return (
            hasTransitionFromExcluded( delta ) ||
            hasTransitionToExcluded( delta )
        )
    }

    const statusTransitionSetHasActionableItems: UseProjectActivityHook['statusTransitionSetHasActionableItems'] = ( deltas ) => {

        let results = _.compact( deltas.map( statusTransitionIsActionable ) )

        return ( results.length > 0 );
    }

    const periodicRecurrenceStringDescription: UseProjectActivityHook['periodicRecurrenceStringDescription'] = ( item ) => {

        return hasRecurrence( item ) && isPeriodicRecurrence( item )
               ? `Every ${ item.recurrenceValue } ${ TimeService.getHumanizedTimeUnits( item.recurrenceValue, item.recurrenceUnit as unknown as TIME_UNITS ) }`
               : 'No Recurrences';
    }

    const milestoneRecurrenceStringDescription: UseProjectActivityHook['milestoneRecurrenceStringDescription'] = ( item ) => {

        return hasRecurrence( item ) && isMilestoneRecurrence( item )
               ? `Recurs on Milestones`
               : 'No Recurrences';
    }

    // recurrence info
    const hasRecurrence: UseProjectActivityHook['hasRecurrence'] = ( item ) => {
        return !_.isNull( item.recurrenceType );
    }

    // recurrence info
    const isPeriodicRecurrence: UseProjectActivityHook['isPeriodicRecurrence'] = ( item ) => {
        return ( item.recurrenceType === CorvalRecurrenceType.Periodic );
    }

    // recurrence info
    const isMilestoneRecurrence: UseProjectActivityHook['isMilestoneRecurrence'] = ( item ) => {
        return ( item.recurrenceType === CorvalRecurrenceType.Milestone );
    }

    // hireType info
    const getProjectActivityHireTypes: UseProjectActivityHook['getProjectActivityHireTypes'] = ( item ) => {

        let hireTypes             = new Set(),
            extractHireTypeMethod = ( hireTypeEntity: CorvalActivityHire ) => hireTypes.add( hireTypeEntity.hireTypeId );

        if( item.requiredHires && _.isArray( item.requiredHires ) ){

            // Required hires: Either DEM or user defined activity hires
            item.requiredHires.map( extractHireTypeMethod );
        }

        return Array.from( hireTypes ) as string[];
    }

    // tags info
    // @TODO: PA tags not support just yet, remove ignores once it is.
    const getProjectActivityTags: UseProjectActivityHook['getProjectActivityTags'] = ( item ) => {

        let tags             = new Set(),
            extractTagMethod = tagEntity => {
                if( tagEntity?.tag?.name ){
                    tags.add( tagEntity.tag.name );
                }
            };

        //@ts-ignore
        if( item.tags && _.isArray( item.tags ) ){

            // Project Activity tags (which could be curated)
            //@ts-ignore
            item.tags.map( extractTagMethod );

        } else if( item.activity && item.activity.tags && _.isArray( item.activity.tags ) ){

            // Activity tags (which come from the DEM)
            item.activity.tags.map( extractTagMethod );
        }
        return Array.from( tags ) as string[];
    }

    // Duration in readable format
    const getProjectActivityDuration: UseProjectActivityHook['getProjectActivityDuration'] = ( item, asDiscreteObject = false ) => {

        const durationValue  = item?.activity?.defaultTimeSpanValue ?? null,
              durationUnit   = ( item?.activity?.defaultTimeSpanUnit as unknown as TIME_UNITS ) ?? null,
              discreteObject = { durationValue, durationUnit } as { durationValue: number, durationUnit: TIME_UNITS };

        return ( asDiscreteObject )
               ? discreteObject
               : ( durationValue && durationUnit )
                 ? `${ durationValue } ${ TimeService.getHumanizedTimeUnits( durationValue, durationUnit ) }`
                 : ``;
    }

    // Duration in readable format
    const getProjectActivityDurationInDays: UseProjectActivityHook['getProjectActivityDurationInDays'] = ( item ) => {

        const durationValue = item?.activity?.defaultTimeSpanValue ?? null,
              durationUnit  = item?.activity?.defaultTimeSpanUnit ?? null;

        try{
            const now   = moment(),
                  later = moment().add( durationValue, durationUnit as unknown as unitOfTime.Base );

            return later.diff( now, 'days' )

        } catch( e ) {

            // ...
        }

        return null;
    }

    const getUpdateProjectActivityInputData: UseProjectActivityHook['getUpdateProjectActivityInputData'] = ( projectActivity ) => {

        try{

            return {
                id:                            projectActivity.id,
                completionState:               projectActivity.completionState,
                notes:                         projectActivity.notes,
                ongoingCostOverride:           projectActivity.ongoingCostOverride,
                ongoingCostOverrideNote:       projectActivity.ongoingCostOverrideNote,
                operationalCostOverride:       projectActivity.operationalCostOverride,
                operationalCostOverrideNote:   projectActivity.operationalCostOverrideNote,
                perceivedAsContinuousOverride: projectActivity.perceivedAsContinuousOverride,
                recurrenceMilestonesOverride:  _.pluck( projectActivity.recurrenceMilestonesOverride, 'recurrenceMilestone' ),
                recurrenceCustomMilestoneIds:  _.pluck( projectActivity.recurrenceCustomMilestones, 'projectCustomTimingMilestoneId' ),

                startTimeOverrideUtc:                       projectActivity.startTimeOverrideUtc,
                endTimeOverrideUtc:                         projectActivity.endTimeOverrideUtc,
                defaultTimeSpanValueOverride:               projectActivity.defaultTimeSpanValueOverride,
                defaultTimeSpanUnitOverride:                projectActivity.defaultTimeSpanUnitOverride,
                projectTimingMilestoneOverride:             projectActivity.projectTimingMilestoneOverride,
                projectCustomTimingMilestoneId:             projectActivity.projectCustomTimingMilestoneId,
                projectTimingMilestoneOffsetMonthsOverride: projectActivity.projectTimingMilestoneOffsetMonthsOverride,

                recurrenceTypeOverride:  projectActivity.recurrenceTypeOverride,
                recurrenceUnitOverride:  projectActivity.recurrenceUnitOverride,
                recurrenceValueOverride: projectActivity.recurrenceValueOverride,

                hiresOverride: _.isArray( projectActivity.hiresOverride ) ? projectActivity.hiresOverride.map( ho => withoutType( ho ) ) : projectActivity.hiresOverride,

            } as CorvalProjectActivityUpdateItemInput

        } catch( e ) {

            throw new Error( `Project activity item does not have all required properties to form input. ${ e.message }` )
        }

    }

    const hasLateCompletion: UseProjectActivityHook['hasLateCompletion'] = ( scheduledEndTimeUtc, endTimeValue, projectTimelineDateFieldObjects = [] ) => {

        const actualCompletionTime          = TimeService.ISOtoMoment( scheduledEndTimeUtc ),
              proposedCompletionValue       = TimeService.ISOtoMoment( endTimeValue ),
              proposedCompletionIsMilestone = !( proposedCompletionValue.isValid() );

        // return if no valid objects to compare
        if( _.isEmpty( scheduledEndTimeUtc ) || _.isEmpty( endTimeValue ) || !actualCompletionTime.isValid() ) return false;

        try{

            // If we have a milestone date field object, then we need to
            // compare the actual completion time to the milestone date.
            // Otherwise, we have a date field object to compare to.
            if( proposedCompletionIsMilestone ){

                const milestoneDateFieldObject = ProjectService.getTimelineDataObjectFromSet( endTimeValue, projectTimelineDateFieldObjects );

                return milestoneDateFieldObject
                       ? actualCompletionTime.isAfter( TimeService.ISOtoMoment( milestoneDateFieldObject.rawValue ) )
                       : false;

            } else {

                return actualCompletionTime.isAfter( proposedCompletionValue );
            }

        } catch( e ) {

            // ...
        }

        return false;
    }

    const isCriticalActivity: UseProjectActivityHook['isCriticalActivity'] = ( criticalityValue: any ) => {

        try{

            return ( ( criticalityValue as unknown as ACTIVITY_CRITICALITY ) == ACTIVITY_CRITICALITY.CRITICAL )

        } catch( e ) {

            // ...
        }

        return false;
    }

    const getCriticalityIcon: UseProjectActivityHook['getCriticalityIcon'] = ( criticalityValue: CorvalCriticality | ACTIVITY_CRITICALITY ) => {

        const mapping = new Map<CorvalCriticality | ACTIVITY_CRITICALITY, SemanticICONS>( [
            [ ACTIVITY_CRITICALITY.CRITICAL, ENTITY_ICONS.ACTIVITIES.CRITICALITY.CRITICAL ],
            [ ACTIVITY_CRITICALITY.PRIORITY, ENTITY_ICONS.ACTIVITIES.CRITICALITY.PRIORITY ],
            [ ACTIVITY_CRITICALITY.MODERATE, ENTITY_ICONS.ACTIVITIES.CRITICALITY.MODERATE ],
            [ ACTIVITY_CRITICALITY.MINOR, ENTITY_ICONS.ACTIVITIES.CRITICALITY.MINOR ],
            [ CorvalCriticality.Critical, ENTITY_ICONS.ACTIVITIES.CRITICALITY.CRITICAL ],
            [ CorvalCriticality.Priority, ENTITY_ICONS.ACTIVITIES.CRITICALITY.PRIORITY ],
            [ CorvalCriticality.Moderate, ENTITY_ICONS.ACTIVITIES.CRITICALITY.MODERATE ],
            [ CorvalCriticality.Minor, ENTITY_ICONS.ACTIVITIES.CRITICALITY.MINOR ],
        ] );

        return mapping.has( criticalityValue )
               ? mapping.get( criticalityValue )
               : mapping.get( criticalityValue )
    }

    const getCriticalityColor: UseProjectActivityHook['getCriticalityColor'] = ( criticalityValue: CorvalCriticality | ACTIVITY_CRITICALITY ) => {

        const mapping = new Map<CorvalCriticality | ACTIVITY_CRITICALITY, CorvalColor>( [
            [ ACTIVITY_CRITICALITY.CRITICAL, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.CRITICAL ],
            [ ACTIVITY_CRITICALITY.PRIORITY, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.PRIORITY ],
            [ ACTIVITY_CRITICALITY.MODERATE, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.MODERATE ],
            [ ACTIVITY_CRITICALITY.MINOR, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.MINOR ],
            [ CorvalCriticality.Critical, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.CRITICAL ],
            [ CorvalCriticality.Priority, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.PRIORITY ],
            [ CorvalCriticality.Moderate, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.MODERATE ],
            [ CorvalCriticality.Minor, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.MINOR ],
        ] );

        return mapping.has( criticalityValue )
               ? mapping.get( criticalityValue )
               : mapping.get( criticalityValue )
    }

    const getProjectActivityStatusInfo: UseProjectActivityHook['getProjectActivityStatusInfo'] = ( activity ) => {

        let { VALUE: statusValue, ICON: statusIcon, COLOR: statusColor, LABEL: statusLabel } = ACTIVITY_STATUS_INFO[ activity.completionState ],
            isNotStarted                                                                     = ( activity.completionState as unknown as ACTIVITY_STATUS ) === ACTIVITY_STATUS.NOTSTARTED,
            isDeferred                                                                       = ( activity.completionState as unknown as ACTIVITY_STATUS ) === ACTIVITY_STATUS.EXCLUDED,
            isComplete                                                                       = ( activity.completionState as unknown as ACTIVITY_STATUS ) === ACTIVITY_STATUS.COMPLETE,
            isInProgress                                                                     = !( isComplete || isDeferred || isNotStarted )

        let { isRecurrence }       = activity,
            isNotStartedRecurrence = ( isRecurrence && isNotStarted );

        const defaultActivityColor = ( isNotStartedRecurrence ) ? SemanticUIColor.GREY : statusColor,
              activityActiveColor  = SemanticUIColor.ORANGE;

        return {
            isNotStarted,
            isDeferred,
            isComplete,
            isInProgress,
            activityActiveColor,
            defaultActivityColor,
            statusColor,
            statusIcon,
            statusLabel,
            statusValue: ( statusValue as ACTIVITY_STATUS ),
        }
    }

    const getStatusIcon: UseProjectActivityHook['getStatusIcon'] = ( completionState ) => {

        const { ICON: statusIcon } = ACTIVITY_STATUS_INFO[ completionState ];

        return statusIcon;
    };

    const getStatusColor: UseProjectActivityHook['getStatusColor'] = ( completionState ) => {

        const { COLOR: statusColor } = ACTIVITY_STATUS_INFO[ completionState ];

        return statusColor;
    };

    const getChangeSetProperties: UseProjectActivityHook['getChangeSetProperties'] = ( changeSetType, projectId ) => {

        const propertyMap = {
            recurrences: `
                isRecurrence
                recurrenceUnit
			    recurrenceValue
			    recurrenceType
			    recurrenceMilestones{
			        recurrenceMilestone
			    }
				recurrenceCustomMilestones{
					projectCustomTimingMilestoneId
					projectCustomTimingMilestone {
						name
						date
					}
				}
                recurrenceSourceProjectActivityId
                recurrenceSourceProjectActivity {
                    activity {
                        displayName
                    }
                }`,

            activityTimingDisplay: `
                perceivedAsContinuous`,

            activityResourcing: `
                requiredHires {
				   headcount
				   hireType { 
				        id 
				        ${ getFieldWithProjectIdAccessor( projectId, 'name' ) }
				        ${ getFieldWithProjectIdAccessor( projectId, 'description' ) } 
				        businessFunction { id }
				        salary 
				        modifier 
                    }
				}`,

            activityTiming: `
                startTimeUtc
                endTimeUtc
                displayName
                completionState
                activityId
                isRecurrence
                childProjectActivities{
                    id activity {
                        id
                        parentActivities{
                            parentActivityId
                            parentCompletionRequired
                            parentSchedulingEnforced
                        }
                    }
                }`,

            budget: `
                requiredHires {
				   headcount
				   hireType { 
				        id 
				        ${ getFieldWithProjectIdAccessor( projectId, 'name' ) }
				        ${ getFieldWithProjectIdAccessor( projectId, 'description' ) }
				        businessFunction { id }
				        salary 
				        modifier 
                    }
				}
                operationalCostOverride
				operationalCostOverrideNote
				ongoingCostOverride
				ongoingCostOverrideNote
				professionalFees {
					id
					feeDate
					amount
					professionalFee{
						id
						name
						description
						resourceCode
						amount
					}
				}`
        };

        return propertyMap[ changeSetType ] ?? null;
    }

    const bulkUpdateProjectActivities: UseProjectActivityHook['bulkUpdateProjectActivities'] = ( projectId, projectVersionId, projectActivityIds, type, onUpdateComplete = null, onUpdateError = null, modalProps = {} ) => {

        modalActions.show(
            <ProjectActivityBulkUpdateDialog
                onUpdateComplete={ onUpdateComplete }
                onUpdateError={ onUpdateError }
                updateType={ type }
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                projectActivityIds={ projectActivityIds }
            />, {
                size: 'small',
                ...modalProps
            }
        );
    };

    const bulkUpdateProjectActivitiesMulti: UseProjectActivityHook['bulkUpdateProjectActivitiesMulti'] = ( projectId, projectVersionId, projectActivityIds, onUpdateComplete = null, onUpdateError = null, showOptionsForTypes = true, modalProps = {} ) => {

        modalActions.show(
            <ProjectActivityBulkUpdateMultiDialog
                onUpdateComplete={ onUpdateComplete }
                onUpdateError={ onUpdateError }
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                projectActivityIds={ projectActivityIds }
                showOptionsForTypes={ showOptionsForTypes }
            />, {
                size:  'fullscreen',
                style: { maxWidth: `80vw` },
                ...modalProps
            }
        );
    };

    const getProjectActivityHasDependencyLoops: UseProjectActivityHook['getProjectActivityHasDependencyLoops'] = async ( activityId, parentIds, childIds ) => {

        if( _.isEmpty( activityId ) || _.isEmpty( parentIds ) || _.isEmpty( childIds ) ) return null;

        const loopCheckReq = await apolloClient.query( {
            query:       DecisionEngineQueries.queries.CHECK_ACTIVITY_DEPENDENCY_LOOP_DETECTION(),
            variables:   {
                id:                activityId,
                parentActivityIds: parentIds,
                childActivityIds:  childIds,
                tracePaths:        true,
            },
            fetchPolicy: 'network-only',
        } )

        try{

            const { loopsDetected } = loopCheckReq.data?.activityRelationshipLoops;

            return loopsDetected as boolean;

        } catch( e ) {

            // ...
        }

        return null;
    }

    return {
        hasTransitionFromExcluded: hasTransitionFromExcluded,
        hasTransitionToExcluded:   hasTransitionToExcluded,
        statusTransitionIsActionable,
        statusTransitionSetHasActionableItems,
        periodicRecurrenceStringDescription,
        milestoneRecurrenceStringDescription,
        hasRecurrence,
        isPeriodicRecurrence,
        isMilestoneRecurrence,
        getProjectActivityTags,
        getUpdateProjectActivityInputData,
        getProjectActivityDuration,
        getProjectActivityDurationInDays,
        hasLateCompletion,
        isCriticalActivity,
        getCriticalityIcon,
        getCriticalityColor,
        getProjectActivityStatusInfo,
        getProjectActivityHireTypes,
        getStatusIcon,
        getStatusColor,
        getChangeSetProperties,
        bulkUpdateProjectActivities,
        bulkUpdateProjectActivitiesMulti,
        getProjectActivityHasDependencyLoops,
    }
};
